/**
 * Google Ads conversion event definitions
 *
 * All conversions are tracked via GA4 Measurement Protocol (server-side),
 * then imported into Google Ads via GA4 linking.
 */

/**
 * Enum of all tracked Google Ads conversion events
 */
export enum GoogleAdsConversionEvent {
    LENDING_PHONE_VERIFICATION = 'Lending_Phone_Verification',
    LENDING_BANK_CONNECTION = 'Lending_Bank_Connection',
    LENDING_APPROVED = 'Lending_Approved',
    LENDING_SUBSCRIPTION_CONFIRMED = 'Lending_Subscription_Confirmed',
    LENDING_KYC_SUCCESS = 'Lending_KYC_Success',
    LENDING_USER_VERIFIED = 'Lending_User_Verified',
}

/**
 * Configuration for a conversion action
 */
export interface ConversionConfig {
    /** Track conversion value (e.g., loan amount) */
    trackValue?: boolean
    /** Default currency for value tracking */
    currency?: string
}

/**
 * Configuration for each conversion event
 */
export const CONVERSION_CONFIG: Record<
    GoogleAdsConversionEvent,
    ConversionConfig
> = {
    [GoogleAdsConversionEvent.LENDING_PHONE_VERIFICATION]: {},
    [GoogleAdsConversionEvent.LENDING_BANK_CONNECTION]: {},
    [GoogleAdsConversionEvent.LENDING_APPROVED]: {
        trackValue: true,
        currency: 'USD',
    },
    [GoogleAdsConversionEvent.LENDING_SUBSCRIPTION_CONFIRMED]: {},
    [GoogleAdsConversionEvent.LENDING_KYC_SUCCESS]: {},
    [GoogleAdsConversionEvent.LENDING_USER_VERIFIED]: {},
}

// Backwards compatibility alias
export const CONVERSION_LABELS = CONVERSION_CONFIG

/**
 * Parameters for tracking a conversion
 */
export interface ConversionParams {
    /** Conversion value (e.g., loan amount) */
    value?: number
    /** Currency code (default: USD) */
    currency?: string
    /** Transaction ID for deduplication */
    transactionId?: string
}

/**
 * GA4 event name mapping for server-side tracking via Measurement Protocol.
 * These names are sent to GA4 and then imported into Google Ads.
 */
export const GA4_EVENT_NAMES: Record<GoogleAdsConversionEvent, string> = {
    [GoogleAdsConversionEvent.LENDING_PHONE_VERIFICATION]:
        'lending_phone_verification',
    [GoogleAdsConversionEvent.LENDING_BANK_CONNECTION]:
        'lending_bank_connection',
    [GoogleAdsConversionEvent.LENDING_APPROVED]: 'lending_approved',
    [GoogleAdsConversionEvent.LENDING_SUBSCRIPTION_CONFIRMED]:
        'lending_subscription_confirmed',
    [GoogleAdsConversionEvent.LENDING_KYC_SUCCESS]: 'lending_kyc_success',
    [GoogleAdsConversionEvent.LENDING_USER_VERIFIED]: 'lending_user_verified',
}

/**
 * Gets the GA4 event name for a conversion event, with environment prefix.
 * - Production (prd): Uses unprefixed event names (e.g., 'lending_approved')
 * - Other environments: Uses 'dev_' prefix (e.g., 'dev_lending_approved')
 *
 * @param event - The conversion event to get the GA4 name for
 * @returns The GA4 event name with appropriate environment prefix
 */
export function getGA4EventName(event: GoogleAdsConversionEvent): string {
    const baseName = GA4_EVENT_NAMES[event]
    const isProduction = process.env.NEXT_PUBLIC_ENV === 'prd'
    return isProduction ? baseName : `dev_${baseName}`
}
